import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import UserAvatar from "assets/images/Display Picture Variants.png";
import Logo from "assets/images/Logo.svg";
import SLetter from "assets/images/signality_letters/S.svg";
import ILetter from "assets/images/signality_letters/I.svg";
import GLetter from "assets/images/signality_letters/G.svg";
import NLetter from "assets/images/signality_letters/N.svg";
import ALetter from "assets/images/signality_letters/A.svg";
import LLetter from "assets/images/signality_letters/L.svg";
import TLetter from "assets/images/signality_letters/T.svg";
import YLetter from "assets/images/signality_letters/Y.svg";
import Pin from "assets/images/pin.svg";
import Unpin from "assets/images/unpin.svg";
import { useTranslation } from "react-i18next";
import { clearTokens, clearUser } from "utils/auth.js";
import { sidebarLinks, secondaryLinks } from "./sidebarConfig";

const isPathActive = (pathname, targets = []) => {
  return targets.some((t) => pathname === t || pathname.startsWith(t));
};

const SIGNALITY_LETTERS = [
  { src: SLetter, alt: "S" },
  { src: ILetter, alt: "I" },
  { src: GLetter, alt: "G" },
  { src: NLetter, alt: "N" },
  { src: ALetter, alt: "A" },
  { src: LLetter, alt: "L" },
  { src: ILetter, alt: "I" },
  { src: TLetter, alt: "T" },
  { src: YLetter, alt: "Y" },
];

const Sidebar = (props) => {
  const { onTogglePin, pinned, profile } = props;
  const { t } = useTranslation();
  const location = useLocation();
  const [me, setMe] = useState(null);
  const nav = useNavigate();
  const handleLogout = () => {
    clearTokens();
    clearUser();
    nav("/login");
  };
  useEffect(() => {
    setMe(profile || null);
  }, [profile]);

  const allowedModules = useMemo(
    () => (me?.allowed_modules || []).map((m) => (m || "").toLowerCase()),
    [me]
  );

  const buildActiveTargets = (link) => {
    const base = link.route || "";
    const childRoutes = (link.children || []).map((child) => {
      if (!child.route) return base;
      const trimmedBase = base.endsWith("/") ? base.slice(0, -1) : base;
      const childPath = child.route.startsWith("/") ? child.route : `/${child.route}`;
      return `${trimmedBase}${childPath}`;
    });
    return [base, ...childRoutes];
  };

  const isLinkAllowed = (link) => {
    const moduleKey = (link.module || "").toLowerCase();
    if (!moduleKey || moduleKey === "logout") return true;
    if (!allowedModules.length) return true;
    return allowedModules.includes(moduleKey);
  };

  const renderLinks = (links = []) => links.filter((link) => !link.hidden).filter(isLinkAllowed).map((link) => {
    const active = isPathActive(location.pathname, buildActiveTargets(link));
    const isLogout = (link.module || "").toLowerCase() === "logout" || link.route === "/logout";
    const isDisabled = !!link.disabled;
    const to = isLogout ? "/login" : isDisabled ? location.pathname : link.route;
    const onClick = isLogout
      ? (e) => { e.preventDefault(); handleLogout(); }
      : isDisabled
        ? (e) => e.preventDefault()
        : undefined;

    const label = t(link.labelKey || link.label, { defaultValue: link.label });

    return (
      <li key={link.route} className={active ? "active_list active" : ""}>
        <Link
          to={to}
          onClick={onClick}
          className="d-flex align-items-center"
          aria-disabled={isDisabled}
        >
          <img src={link.icon} alt={label} className="sidebar-icon" />
          <span className="nav_list_text">{label}</span>
        </Link>
        {link.badge && <span className="badge">{link.badge}</span>}
      </li>
    );
  });

  return (
    <div className="sidebar__menu">
      <div
        className="pin_icon mb-4 d-flex justify-content-end"
        onClick={onTogglePin}
        style={{ cursor: "pointer" }}
      >
        <img src={pinned ? Pin : Unpin} alt={t("sidebar.pinToggle")} className="img-fluid" />
      </div>
      {/* Brand */}
      <div className="brand_section p-2 d-flex align-items-center gap-2 mb-4">
        <img src={Logo} alt={t("sidebar.logoAlt")} className="img-fluid" />
        <div className="brand_text brand_wordmark" aria-label={t("sidebar.brandName")}>
          {SIGNALITY_LETTERS.map((letter, idx) => (
            <img key={`${letter.alt}-${idx}`} src={letter.src} alt={letter.alt} className="brand_letter" />
          ))}
        </div>
      </div>

      <div className="sidebar__menu-sections d-flex flex-column justify-content-between gap-4">
        <ul className="ps-0 sidebar__menu__list">
          {renderLinks(sidebarLinks)}
        </ul>

        <ul className="ps-0 sidebar__menu__list">
          {renderLinks(secondaryLinks)}
        </ul>
      </div>

      {/* User Section */}
      <div className="user_section d-flex align-items-center gap-2 pt-4">
        <img src={UserAvatar} alt={t("sidebar.userAlt")} className="user_avatar" />
        <div className="user_detail">
          <h6 className="text-white ">{me ? `${me.first_name ?? ''} ${me.last_name ?? ''}`.trim() || me.email : '—'}</h6>
          <small className="text-white">{me?.email ?? ''}</small>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
